import { StandardEvents } from '@shopify/events';

class MistaraProductPage extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const variantsNode = this.querySelector('[data-variants-json]');
    this.variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
    this.variantSelect = this.querySelector('[data-variant-select]');
    this.variantChoices = this.querySelectorAll('[data-variant-choice]');
    this.packSummary = this.querySelector('[data-pack-summary]:not(input)');
    this.variantInput = this.querySelector('[data-variant-id-input]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.stock = this.querySelector('[data-stock]');
    this.stockText = this.querySelector('[data-stock-text]');
    this.addButton = this.querySelector('[ref="addToCartButton"]');
    this.addLabel = this.querySelector('[data-add-to-cart-label]');
    this.acceleratedCheckout = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    this.quantitySelector = this.querySelector('quantity-selector-component');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.addToCartComponent = this.querySelector('add-to-cart-component');

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(({ detail }) => {
        if (signal.aborted || !Array.isArray(detail?.items)) return;
        this.variants.forEach((variant) => {
          variant.cartQuantity = detail.items.reduce((quantity, item) =>
            quantity + (String(item.variant_id) === String(variant.id) ? Number(item.quantity) || 0 : 0), 0);
        });
      }).catch(() => {});
    }, { signal });

    this.variantSelect?.addEventListener('change', () => this.updateVariant(this.variantSelect.value), { signal });
    this.variantChoices.forEach((choice) => {
      choice.addEventListener('change', () => {
        if (choice.checked) this.updateVariant(choice.value);
      }, { signal });
    });
    this.syncVariantChoices(this.variantInput?.value || this.variantSelect?.value);
    this.querySelectorAll('[data-media-target]').forEach((button) => {
      button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget), { signal });
    });
    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.showRelativeMedia(-1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showRelativeMedia(1), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  updateVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    this.syncVariantChoices(variant.id);
    if (this.variantInput) this.variantInput.value = variant.id;
    const productForm = this.querySelector('product-form-component');
    if (productForm) productForm.dataset.quantityDefault = String(variant.quantityMin || 1);
    if (this.price) this.price.textContent = variant.price;
    if (this.comparePrice) {
      this.comparePrice.hidden = !variant.compareAt;
      this.comparePrice.textContent = variant.compareAt || '';
    }
    if (this.stock) this.stock.dataset.stockState = variant.stockState;
    if (this.stockText) this.stockText.textContent = variant.stockText;
    if (this.addButton) this.addButton.disabled = !variant.available;
    if (this.addLabel) {
      this.addLabel.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
    if (this.acceleratedCheckout) this.acceleratedCheckout.hidden = !variant.available;

    if (this.quantityInput) {
      this.quantityInput.min = String(variant.quantityMin || 1);
      this.quantityInput.dataset.min = String(variant.quantityMin || 1);
      this.quantityInput.step = String(variant.quantityIncrement || 1);
      this.quantityInput.value = String(variant.quantityMin || 1);
      this.quantityInput.setAttribute('data-cart-quantity', String(variant.cartQuantity || 0));
      this.quantityInput.disabled = !variant.available;
      if (variant.quantityMax !== null) this.quantityInput.max = String(variant.quantityMax);
      else this.quantityInput.removeAttribute('max');
    }

    if (this.quantitySelector) {
      this.quantitySelector.dataset.variantId = String(variant.id);
      this.quantitySelector.setCartQuantity?.(variant.cartQuantity || 0);
      if (variant.available) {
        this.quantitySelector.serverDisabledMinus = false;
        this.quantitySelector.serverDisabledPlus = false;
        this.quantitySelector.updateConstraints?.(
          String(variant.quantityMin || 1),
          variant.quantityMax === null ? null : String(variant.quantityMax),
          String(variant.quantityIncrement || 1),
        );
      } else {
        this.quantitySelector.querySelectorAll('button').forEach((button) => {
          button.disabled = true;
        });
      }
    }
    if (this.addToCartComponent && variant.mediaUrl) {
      this.addToCartComponent.dataset.productVariantMedia = variant.mediaUrl;
    }
    if (variant.mediaId) this.showMedia(variant.mediaId);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url);
  }

  syncVariantChoices(variantId) {
    if (this.variantSelect) this.variantSelect.value = String(variantId);
    let selectedChoice;
    this.variantChoices.forEach((choice) => {
      choice.checked = String(choice.value) === String(variantId);
      if (choice.checked) selectedChoice = choice;
    });
    if (this.packSummary) {
      const summary = selectedChoice?.dataset.packSummary || '';
      this.packSummary.textContent = summary;
      this.packSummary.hidden = !summary;
    }
  }

  showRelativeMedia(direction) {
    const media = Array.from(this.querySelectorAll('.mistara-pdp__media[data-media-id]'));
    if (media.length < 2) return;

    const activeIndex = media.findIndex((item) => !item.classList.contains('mistara-pdp__media--hidden'));
    const nextIndex = (activeIndex + direction + media.length) % media.length;
    this.showMedia(media[nextIndex].dataset.mediaId);
  }

  showMedia(mediaId) {
    this.querySelectorAll('.mistara-pdp__media[data-media-id]').forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.classList.toggle('mistara-pdp__media--hidden', !active);
      media.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!active) {
        media.querySelectorAll('deferred-media, product-model').forEach((player) => player.pauseMedia?.());
        media.querySelectorAll('video').forEach((video) => video.pause());
      }
    });
    this.querySelectorAll('[data-media-target]').forEach((button) => {
      const active = String(button.dataset.mediaTarget) === String(mediaId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior });
      }
    });
  }
}

if (!customElements.get('mistara-product-page')) {
  customElements.define('mistara-product-page', MistaraProductPage);
}
